import * as React from "react";
import { RouterLink as Link } from "../router";

import { cn } from "../utils";
import { MetaFacts, type MetaFact } from "./meta-facts";
import { NavTabs, type NavTab } from "../nav-tabs";
import { ArrowLeft } from "../icons";

/**
 * Masthead — the ONE page-title shape, promoted verbatim from the
 * `/styleguide/workflow-detail` `DetailHeader` prototype (Matt: "every single
 * page needs to have the same options and data that exist on workflow-detail,
 * and it should be DRY"). It is purely presentational: three vertical bands with
 * the prototype's tuned ASCENDING rhythm, and no shell/portal/route knowledge of
 * its own.
 *
 *   ← Back                                   (back eyebrow · mb-3 = 12px)
 *   Title                    [ actions ]     (identity band · gap-2)
 *   One-line description
 *   ◧ Domain  🛡 Gate  👤 Runs as  📡 Live
 *   ─────────────────────────────────────
 *   (Overview)(Pipeline)(Runs)      [30 days ▾]   (nav band · mt-6)
 *
 * The two shipped hosts compose it:
 *  · `PageHeader` (standalone pages) wraps the identity band in
 *    `HideWhenShellTitled` (via `identityWrap`) and portals `actions` through
 *    `ShellActionsPortal`.
 *  · `SectionTitleBar` (mode-shell title) renders it plainly and hangs the live
 *    `ShellActionsSlot` off `actions`.
 * Both feed the SAME `MetaFacts` + `NavTabs` primitives, so there is one facts
 * strip and one pill nav in the whole product. See
 * docs/design/PAGE-HEADER-UNIFICATION-PRD.md.
 */

/** The "← Parent" back eyebrow — a real `<Link>` when given `href`, a `<button>`
 *  when given `onClick` (the prototype's local-state form). */
function BackEyebrow({
  label,
  href,
  onClick,
  className,
}: {
  label: string;
  href?: string;
  onClick?: () => void;
  className?: string;
}) {
  const cls = cn(
    "group inline-flex w-fit items-center gap-1 type-subtext text-muted-foreground transition-colors hover:text-foreground",
    className,
  );
  const inner = (
    <>
      <ArrowLeft className="size-3" />
      {label}
    </>
  );
  return href ? (
    <Link href={href} className={cls}>
      {inner}
    </Link>
  ) : (
    <button type="button" onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}

export interface MastheadProps {
  /** The "← Parent" back eyebrow above the title (a single up-link). */
  back?: { label: string; href?: string; onClick?: () => void };
  /** Optional tiny letterspaced eyebrow between the back link and the title. */
  eyebrow?: React.ReactNode;
  /** The page title, in the unified serif `type-page-title` role. */
  title?: React.ReactNode;
  /** Semantic sibling of the title, such as an object's lifecycle badge. */
  titleAccessory?: React.ReactNode;
  /** Extra classes appended to the title `<h1>` (layout only — never a size). */
  titleClassName?: string;
  /** One-line description under the title (what the page is FOR). */
  description?: React.ReactNode;
  /** Icon meta-facts under the description (Domain · Gate · Runs as · Live…). */
  meta?: MetaFact[];
  /** Right-aligned controls beside the title (CTA, on/off, filters, ⋯ menu). */
  actions?: React.ReactNode;
  /** In-page sub-nav below the masthead, rendered through the canonical NavTabs. */
  tabs?: NavTab[];
  /** Controlled-mode selection for `tabs` (local state → buttons, no navigation). */
  navValue?: string;
  onNavSelect?: (key: string) => void;
  /** Slot to the right of `tabs` (a report-range select, a kbd hint…). */
  navRight?: React.ReactNode;
  /**
   * Wrap the identity block (back + eyebrow + title). `PageHeader` passes
   * `HideWhenShellTitled` so a standalone page mounted under a mode shell drops
   * its duplicate H1 while keeping its description/meta as scope context. Default
   * passthrough (the standalone / section-title case, where the title is shown).
   */
  identityWrap?: (node: React.ReactNode) => React.ReactNode;
  className?: string;
}

const passthrough = (node: React.ReactNode) => node;

export function Masthead({
  back,
  eyebrow,
  title,
  titleAccessory,
  titleClassName,
  description,
  meta,
  actions,
  tabs,
  navValue,
  onNavSelect,
  navRight,
  identityWrap = passthrough,
  className,
}: MastheadProps) {
  const hasTabs = tabs && tabs.length > 0;
  return (
    <header data-slot="masthead" className={cn("flex flex-col", className)}>
      {/* ── back band: the eyebrow gets its OWN row above the identity band, so the
          identity band's right-aligned actions align with the TITLE, not the
          eyebrow (the workflow-detail rhythm). mb-3 = 12px to the title — the ONE
          eyebrow→title gap, matched by the mono eyebrow below (Matt, 2026-07-23:
          "the eyebrow should have consistent spacing, like 12px"). ── */}
      {back &&
        identityWrap(
          <BackEyebrow
            label={back.label}
            href={back.href}
            onClick={back.onClick}
            className="mb-3"
          />,
        )}
      {/* ── identity band: [eyebrow · title · description · meta] | [actions] ── */}
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-6">
        <div className="flex min-w-0 flex-col gap-2">
          {identityWrap(
            <>
              {eyebrow && (
                // `mb-1` on top of the column's `gap-2` (8px + 4px) makes the
                // mono eyebrow → title gap 12px, matching the back eyebrow's
                // `mb-3` so BOTH eyebrow treatments sit the same 12px above the
                // title. The gap to a following description/meta stays gap-2.
                <div className="mode-title-eyebrow mb-1 text-muted-foreground/70">
                  {eyebrow}
                </div>
              )}
              {(title || titleAccessory) && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  {title && (
                    <h1
                      data-slot="masthead-title"
                      className={cn("type-page-title text-foreground", titleClassName)}
                    >
                      {title}
                    </h1>
                  )}
                  {titleAccessory}
                </div>
              )}
            </>,
          )}
          {description && (
            <p className="max-w-2xl type-body text-muted-foreground">{description}</p>
          )}
          {meta && meta.length > 0 && <MetaFacts facts={meta} />}
        </div>
        {actions}
      </div>

      {/* ── nav band: pill sub-nav (+ optional right slot) ── */}
      {hasTabs && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <NavTabs
            tabs={tabs}
            value={navValue}
            onSelect={onNavSelect}
            className="flex-wrap"
          />
          {navRight}
        </div>
      )}
    </header>
  );
}
